import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { languages } from '@/translations';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const location = useLocation();
  const { language, changeLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.suites'), path: '/suites' },
    { name: t('nav.history'), path: '/historia' },
    { name: t('nav.experiences'), path: '/experiencias' },
    { name: t('nav.gallery'), path: '/galeria' },
    { name: t('nav.contact'), path: '/contato' },
  ];

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-white/90 backdrop-blur-md'
      } border-b border-gray-200`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-logo-link">
            <img 
              src="/fechadura.png" 
              alt="Pousada do Mondego" 
              className="w-10 h-14 transition-transform group-hover:scale-110"
            />
            <h1 className="text-xl font-bold tracking-tight text-slate-900 uppercase">
              Pousada do Mondego
            </h1>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-semibold transition-colors hover:text-[#C5A059] ${
                  location.pathname === link.path ? 'text-[#C5A059]' : 'text-slate-900'
                }`}
                data-testid={`navbar-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Language Switcher & CTA */}
          <div className="hidden md:flex items-center gap-3">
            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setShowLangMenu(!showLangMenu)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                data-testid="language-switcher-button"
              >
                <Globe className="w-4 h-4 text-[#C5A059]" />
                <span className="text-sm font-semibold text-slate-900 uppercase">{language}</span>
              </button>
              
              {showLangMenu && (
                <div 
                  className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-[140px] z-50"
                  data-testid="language-menu"
                >
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        changeLanguage(lang.code);
                        setShowLangMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 ${
                        language === lang.code ? 'bg-gray-50 text-[#C5A059] font-semibold' : 'text-slate-700'
                      }`}
                      data-testid={`language-option-${lang.code}`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Reserve Button */}
            <a
              href="/reserva.html"
              target="_blank"
              rel="noopener"
              referrerPolicy="no-referrer-when-downgrade"
              className="flex items-center justify-center rounded-lg h-10 px-6 btn-gold text-sm font-bold shadow-lg"
              data-testid="navbar-reserve-button"
            >
              {t('nav.reserveNow')}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="mobile-menu-button"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 shadow-xl" data-testid="mobile-menu">
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block text-base font-semibold py-2 transition-colors hover:text-[#C5A059] ${
                  location.pathname === link.path ? 'text-[#C5A059]' : 'text-slate-900'
                }`}
                data-testid={`mobile-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Mobile Language Selector */}
            <div className="pt-4 border-t border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Language</p>
              <div className="flex gap-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      changeLanguage(lang.code);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                      language === lang.code
                        ? 'bg-[#C5A059] text-white'
                        : 'bg-gray-100 text-slate-700 hover:bg-gray-200'
                    }`}
                    data-testid={`mobile-language-${lang.code}`}
                  >
                    <span>{lang.flag}</span>
                    <span>{lang.code.toUpperCase()}</span>
                  </button>
                ))}
              </div>
            </div>
            
            <a
              href="/reserva.html"
              target="_blank"
              rel="noopener"
              referrerPolicy="no-referrer-when-downgrade"
              className="block w-full text-center rounded-lg py-3 btn-gold text-sm font-bold"
              data-testid="mobile-reserve-button"
            >
              {t('nav.reserveNow')}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
